// v13 (handoff 17) «Διαγραφή λανθασμένου συμβολαίου» — delete a wrongly entered policy, or restore it.
// Step 1: the CRM password again (or a passkey); step 2: a fresh authenticator code — both checked by the
// platform, which returns a 5-minute single-use grant; step 3: the exact policy, a reason and a typed confirmation
// word. The deletion is a SOFT deletion that the sync server accepts only with the grant; this dialog is
// convenience, not the security. Without an account (offline workspace) deletion is not offered: the policy can be
// marked «cancelled — entered in error» instead.
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  POLICY_RESTORE_DAYS, canRestorePolicy, deletePolicy, markPolicyEnteredInError, policyIdentity, policyLinkedRecords, policyProductionPuts, restoreDeadline, restorePolicy,
  type TableName,
} from '@vero/domain';
import { PlatformError } from '@vero/platform';
import type { Workspace } from '@vero/store';
import { useRuntime } from '../core/runtime.tsx';
import { Field, Modal } from '../core/ui.tsx';
import { Icon } from '../core/icons.tsx';
import { accountService } from '../core/account-service.ts';
import { syncService } from '../core/sync-service.ts';
import { getPasskey, passkeysSupported } from '../core/passkey.ts';
import { MfaSetup } from './account.tsx';

type Ev = { target: HTMLInputElement | HTMLTextAreaElement };
type Mode = 'delete' | 'restore';
type Gate = 'checking' | 'offline' | 'no_sync' | 'mfa' | 'locked' | 'ready' | 'error';
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const errCode = (e: unknown) => (e instanceof PlatformError ? e.message : e instanceof TypeError ? 'network' : String((e as Error)?.message ?? e));

/** wait until this policy's queued change has reached the server; 'refused' when the server turned it down */
async function confirmOnServer(ws: Workspace, policyId: string): Promise<'ok' | 'pending' | { refused: string }> {
  const sync = syncService();
  if (!sync) return 'pending';
  for (let i = 0; i < 12; i++) {
    await sync.syncNow();
    const ops = ws.view.rows('sync_ops').filter((o) => o.table === 'policies' && o.rowId === policyId);
    const refused = ops.filter((o) => o.state === 'rejected').sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))[0];
    if (refused && !ops.some((o) => o.state === 'pending')) return { refused: String(refused.error ?? '').replace(/^forbidden:\s*/, '') };
    if (!ops.some((o) => o.state === 'pending')) return 'ok';
    await sleep(600);
  }
  return 'pending';
}

/** why deletion is or is not possible on this device right now */
function useGate(policyId: string, mode: Mode, retry: number) {
  const [gate, setGate] = useState<Gate>('checking');
  const [info, setInfo] = useState<{ passkeys: boolean; lockedUntil: string | null; err?: string }>({ passkeys: false, lockedUntil: null });
  useEffect(() => {
    let on = true;
    void (async () => {
      const acct = accountService(), sync = syncService();
      if (!acct?.signedIn) { if (on) setGate('offline'); return; }
      if (!sync?.paired) { if (on) setGate('no_sync'); return; }
      try {
        // the server binds the grant to the policy as IT has it: send pending changes of this policy first
        if (mode === 'delete' && sync.ws.view.rows('sync_ops').some((o) => o.state === 'pending' && o.rowId === policyId)) await confirmOnServer(sync.ws, policyId);
        const st = await acct.api.stepUpStatus();
        if (!on) return;
        setInfo({ passkeys: st.passkeys && passkeysSupported(), lockedUntil: st.lockedUntil });
        setGate(!st.mfaEnabled ? 'mfa' : st.locked ? 'locked' : 'ready');
      } catch (e) { if (on) { setInfo((x) => ({ ...x, err: errCode(e) })); setGate('error'); } }
    })();
    return () => { on = false; };
  }, [policyId, mode, retry]);
  return { gate, info };
}

export function PolicyDeleteFlow({ policyId, mode = 'delete' }: { policyId: string; mode?: Mode }) {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const [retry, setRetry] = useState(0);
  const { gate, info } = useGate(policyId, mode, retry);
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [password, setPassword] = useState('');
  const [credential, setCredential] = useState<Record<string, unknown> | null>(null);
  const [code, setCode] = useState('');
  const [grant, setGrant] = useState<{ grant: string; expiresAt: string } | null>(null);
  const [reason, setReason] = useState('');
  const [word, setWord] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [left, setLeft] = useState<number | null>(null);
  const [done, setDone] = useState<{ until: string | null } | null>(null);
  const errRef = useRef<HTMLParagraphElement | null>(null);
  const p = view.get('policies', policyId);
  const id = useMemo(() => (p ? policyIdentity(view, policyId) : null), [view, policyId, p]);
  const linked = useMemo(() => (p ? policyLinkedRecords(view, policyId) : null), [view, policyId, p]);
  const confirmWord = t('policy_delete.word');
  useEffect(() => { if (err) errRef.current?.focus(); }, [err]);
  useEffect(() => {
    if (!grant) return;
    const tick = () => setLeft(Math.max(0, Math.round((new Date(grant.expiresAt).getTime() - Date.now()) / 1000)));
    tick(); const h = setInterval(tick, 1000); return () => clearInterval(h);
  }, [grant]);
  if (!p || !id) return null;
  const title = mode === 'delete' ? t('policy_delete.title') : t('policy_delete.restore_title');
  const msg = (c: string) => { const k = `policy_delete.err.${c}`; const x = t(k); return x === k ? t('policy_delete.err.generic', { code: c }) : x; };

  const usePasskey = async () => {
    setErr(null); setBusy(true);
    try { const o = await accountService()!.api.stepUpPasskeyOptions(); setCredential(await getPasskey(o)); setPassword(''); setStep(2); }
    catch (e) { setErr(msg(errCode(e) === 'passkey_cancelled' ? 'passkey_cancelled' : errCode(e))); }
    finally { setBusy(false); }
  };
  /** steps 1+2 are checked together by the platform; a failure goes back to the step it concerns */
  const verify = async () => {
    setErr(null); setBusy(true);
    try {
      const r = await accountService()!.api.stepUpPolicy({ action: mode === 'delete' ? 'policy.delete' : 'policy.restore', policyId, code: code.replace(/\s+/g, ''), ...(credential ? { credential } : { password }) });
      setGrant({ grant: r.grant, expiresAt: r.expiresAt }); setPassword(''); setCode(''); setStep(3);
    } catch (e) {
      const c = errCode(e);
      if (c === 'invalid_credentials' || c === 'reauth_invalid') { setStep(1); setCredential(null); setCode(''); }
      if (c === 'stepup_locked') setRetry((n) => n + 1);
      setErr(msg(c));
    } finally { setBusy(false); }
  };
  const apply = async () => {
    if (!grant) return;
    setErr(null); setBusy(true);
    const by = accountService()?.snapshot().userId ?? '';
    const ok = await rt.run((v, c) => (mode === 'delete' ? deletePolicy(v, policyId, { reason, grant: grant.grant, by }, c) : restorePolicy(v, policyId, { grant: grant.grant, by, reason }, c)));
    if (!ok) { setBusy(false); return; }
    const r = await confirmOnServer(syncService()!.ws, policyId);
    setBusy(false); setGrant(null);
    if (typeof r === 'object') { setErr(msg(r.refused.split(':')[0]!.trim() || 'refused')); setStep(1); return; }
    const after = syncService()!.ws.view.get('policies', policyId);
    setDone({ until: after ? restoreDeadline(after) : null });
    setStep(4);
    rt.toast(t(mode === 'delete' ? (r === 'ok' ? 'policy_delete.done' : 'policy_delete.done_pending') : 'policy_delete.restored'), 'success');
  };

  // --- not possible here: explain, and offer what is possible -------------------------------------------------------
  if (gate !== 'ready' && step < 4) {
    return (
      <Modal title={title} footer={<button className="btn" onClick={rt.close}>{t('common.close')}</button>}>
        <div className="stack" data-testid="policy-delete-gate" data-gate={gate}>
          {gate === 'checking' && <p className="muted" role="status">{t('policy_delete.checking')}</p>}
          {(gate === 'offline' || gate === 'no_sync') && <>
            <div className="callout warn" role="note"><b>{t('policy_delete.unavailable_title')}</b><p className="small">{t(gate === 'offline' ? 'policy_delete.unavailable_offline' : 'policy_delete.unavailable_nosync')}</p></div>
            {mode === 'delete' && p.status !== 'matured' && <EnteredInError policyId={policyId} />}
          </>}
          {gate === 'mfa' && <div className="callout warn" role="note">
            <b>{t('policy_delete.mfa_title')}</b><p className="small">{t('policy_delete.mfa_body')}</p>
            <button className="btn btn-primary" onClick={() => rt.open(<MfaSetup />)} data-testid="policy-delete-mfa-setup"><Icon name="lock" />{t('policy_delete.mfa_setup')}</button>
          </div>}
          {gate === 'locked' && <div className="callout crit" role="alert">{t('policy_delete.err.stepup_locked')}{info.lockedUntil ? ` ${t('policy_delete.locked_until', { time: i18n.dateTime(info.lockedUntil) })}` : ''}</div>}
          {gate === 'error' && <div className="callout crit" role="alert">{msg(info.err ?? 'network')} <button className="btn btn-sm" onClick={() => setRetry((n) => n + 1)}>{t('policy_delete.retry')}</button></div>}
        </div>
      </Modal>
    );
  }

  const steps = [t('policy_delete.step_password'), t('policy_delete.step_code'), t('policy_delete.step_confirm')];
  const canConfirm = mode === 'restore' ? true : reason.trim().length >= 3 && word.trim().toLocaleUpperCase() === confirmWord.toLocaleUpperCase();
  const footer = step === 4 ? <>
      {mode === 'delete' && done?.until && <button className="btn" onClick={() => rt.open(<PolicyDeleteFlow key={`restore-${Date.now()}`} policyId={policyId} mode="restore" />)} data-testid="policy-restore-open"><Icon name="undo" />{t('policy_delete.restore')}</button>}
      <button className="btn btn-primary" onClick={rt.close} data-testid="policy-delete-close">{t('common.done')}</button></>
    : <>
      <button className="btn" onClick={rt.close}>{t('common.cancel')}</button>
      {step === 1 && <button className="btn btn-primary" disabled={!password} onClick={() => { setErr(null); setStep(2); }} data-testid="policy-delete-next1">{t('common.next')}</button>}
      {step === 2 && <><button className="btn" onClick={() => { setStep(1); setCredential(null); }}>{t('common.back')}</button>
        <button className="btn btn-primary" disabled={busy || !/^\d{6}$/.test(code.replace(/\s+/g, ''))} onClick={() => void verify()} data-testid="policy-delete-verify">{t('policy_delete.verify')}</button></>}
      {step === 3 && <button className={`btn ${mode === 'delete' ? 'btn-danger' : 'btn-primary'}`} disabled={busy || !canConfirm || left === 0} onClick={() => void apply()} data-testid="policy-delete-confirm"><Icon name={mode === 'delete' ? 'trash' : 'undo'} />{mode === 'delete' ? t('policy_delete.confirm') : t('policy_delete.restore')}</button>}
    </>;
  return (
    <Modal title={title} footer={footer}>
      <div className="stack" data-testid="policy-delete-flow" data-mode={mode}>
        {step < 4 && <ol className="steps" aria-label={t('policy_delete.steps')} style={{ listStyle: 'none', padding: 0 }}>{steps.map((s, i) => <li key={s} style={{ display: 'contents' }}><span className={i + 1 === step ? 'on' : ''} aria-current={i + 1 === step ? 'step' : undefined}>{i + 1}. {s}</span></li>)}</ol>}
        {err && <p className="callout crit" role="alert" tabIndex={-1} ref={errRef} data-testid="policy-delete-error">{err}</p>}
        {step === 1 && <>
          <p className="small">{t(mode === 'delete' ? 'policy_delete.intro' : 'policy_delete.restore_intro', { days: POLICY_RESTORE_DAYS })}</p>
          <Field label={t('policy_delete.password')} hint={t('policy_delete.password_hint')}>
            <input className="input" type="password" autoComplete="current-password" value={password} onChange={(e: Ev) => setPassword(e.target.value)} onKeyDown={(e: { key: string }) => { if (e.key === 'Enter' && password) setStep(2); }} data-testid="policy-delete-password" />
          </Field>
          {info.passkeys && <button className="btn btn-ghost" disabled={busy} onClick={() => void usePasskey()} data-testid="policy-delete-passkey"><Icon name="lock" />{t('policy_delete.use_passkey')}</button>}
        </>}
        {step === 2 && <>
          <p className="small">{credential ? t('policy_delete.passkey_ok') : null} {t('policy_delete.code_intro')}</p>
          <Field label={t('policy_delete.code')} hint={t('policy_delete.code_hint')}>
            <input className="input" inputMode="numeric" autoComplete="one-time-code" maxLength={7} value={code} onChange={(e: Ev) => setCode(e.target.value)} onKeyDown={(e: { key: string }) => { if (e.key === 'Enter' && /^\d{6}$/.test(code.replace(/\s+/g, ''))) void verify(); }} data-testid="policy-delete-code" />
          </Field>
        </>}
        {step === 3 && <>
          <p className="small" role="status">{t('policy_delete.verified', { seconds: left ?? 0 })}</p>
          <PolicyIdentityTable id={id} />
          {mode === 'delete' && linked && <LinkedSummary linked={linked} />}
          {mode === 'delete' ? <>
            <Field label={t('policy_delete.reason')} hint={t('policy_delete.reason_hint')}>
              <textarea className="input" rows={2} value={reason} onChange={(e: Ev) => setReason(e.target.value)} required aria-required="true" data-testid="policy-delete-reason" />
            </Field>
            <Field label={t('policy_delete.type_word', { word: confirmWord })}>
              <input className="input" value={word} onChange={(e: Ev) => setWord(e.target.value)} autoComplete="off" spellCheck={false} aria-describedby="pd-word-hint" data-testid="policy-delete-word" />
            </Field>
            <p id="pd-word-hint" className="tiny muted">{t('policy_delete.soft_note', { days: POLICY_RESTORE_DAYS })}</p>
          </> : <Field label={t('policy_delete.restore_reason')}><input className="input" value={reason} onChange={(e: Ev) => setReason(e.target.value)} data-testid="policy-delete-reason" /></Field>}
        </>}
        {step === 4 && <div className="callout ok" role="status" data-testid="policy-delete-done">
          <b>{mode === 'delete' ? t('policy_delete.done') : t('policy_delete.restored')}</b>
          {mode === 'delete' && done?.until && <p className="small">{t('policy_delete.restore_until', { date: i18n.date(done.until.slice(0, 10)) })}</p>}
        </div>}
      </div>
    </Modal>
  );
}

function PolicyIdentityTable({ id }: { id: NonNullable<ReturnType<typeof policyIdentity>> }) {
  const { t, i18n } = useRuntime();
  const d = (x: string | null) => (x ? i18n.date(x) : '—');
  const rows: [string, string][] = [
    [t('policy.carrier'), id.insurer ?? '—'], [t('policy_delete.product'), id.product], [t('policy_delete.number'), id.number ?? '—'],
    [t('policy.owner'), id.holder], [t('policy.insured'), id.insured.join(', ') || '—'],
    [t('policy.start'), d(id.startDate)], [t('policy_delete.issue'), d(id.issueDate)], [t('policy.end'), d(id.endDate)],
    [t('common.status'), i18n.enumLabel('policyStatus', id.status)], [t('policy_delete.premium'), i18n.money(id.annualPremium, id.currency ?? undefined)],
  ];
  return <table className="table" data-testid="policy-delete-identity"><caption className="upper" style={{ textAlign: 'start' }}>{t('policy_delete.identity')}</caption><tbody>{rows.map(([k, v]) => <tr key={k}><th scope="row" className="muted small" style={{ fontWeight: 500 }}>{k}</th><td>{v}</td></tr>)}</tbody></table>;
}

function LinkedSummary({ linked }: { linked: ReturnType<typeof policyLinkedRecords> }) {
  const { t } = useRuntime();
  const fmt = (m: Partial<Record<TableName, number>>) => Object.entries(m).map(([k, n]) => `${t(`policy_delete.table.${k}`)}: ${n}`).join(' · ');
  const hidden = fmt(linked.hidden), kept = fmt(linked.kept);
  if (!hidden && !kept) return <p className="small muted">{t('policy_delete.linked_none')}</p>;
  return <div className="callout small" data-testid="policy-delete-linked">{hidden && <p>{t('policy_delete.linked_hidden')} {hidden}</p>}{kept && <p>{t('policy_delete.linked_kept')} {kept}</p>}</div>;
}

/** the offline alternative: status «cancelled» + «entered in error» (visible, reversible like any status change) */
function EnteredInError({ policyId }: { policyId: string }) {
  const rt = useRuntime();
  const { t } = rt;
  const [reason, setReason] = useState('');
  const save = async () => { if (await rt.run((v, c) => markPolicyEnteredInError(v, policyId, reason, c, (b, a) => policyProductionPuts(v, b, a, c)), 'policy_delete.marked_error')) rt.close(); };
  return (
    <div className="stack" data-testid="policy-entered-in-error">
      <p className="small">{t('policy_delete.error_alt')}</p>
      <Field label={t('policy_delete.reason')}><input className="input" value={reason} onChange={(e: Ev) => setReason(e.target.value)} data-testid="policy-error-reason" /></Field>
      <button className="btn" disabled={reason.trim().length < 3} onClick={() => void save()} data-testid="policy-error-save">{t('policy_delete.mark_error')}</button>
    </div>
  );
}

/** banner inside the policy record of a deleted policy: who, when, why, and restore while the window is open */
export function DeletedPolicyBanner({ policyId }: { policyId: string }) {
  const rt = useRuntime();
  const { t, i18n, view } = rt;
  const p = view.get('policies', policyId);
  if (!p?.deletedAt) return null;
  const until = restoreDeadline(p)!;
  const can = canRestorePolicy(p, new Date().toISOString());
  return (
    <div className="callout crit" role="note" data-testid="policy-deleted-banner">
      <b>{t('policy_delete.deleted_on', { date: i18n.date(p.deletedAt.slice(0, 10)) })}</b>
      {p.deleteReason && <p className="small">{t('policy_delete.reason')}: {p.deleteReason}</p>}
      <p className="small">{can ? t('policy_delete.restore_until', { date: i18n.date(until.slice(0, 10)) }) : t('policy_delete.retained')}</p>
      {can && <button className="btn btn-sm" onClick={() => rt.open(<PolicyDeleteFlow key={`restore-${Date.now()}`} policyId={policyId} mode="restore" />)} data-testid="policy-restore"><Icon name="undo" />{t('policy_delete.restore')}</button>}
    </div>
  );
}
